import type { PaymentMode } from '@dunea/shared';
import httpClient from '~/lib/http-client';

// Owner-facing Reviews plugin API (studio `ReviewsOwnerController`).
// `appId` is the CHAT id. Reviews submitted from the preview are test mode.

export type ReviewStatus = 'pending' | 'approved' | 'rejected';

export interface ReviewView {
  id: string;
  /** Product slug or page key the review is about. */
  subject: string;
  rating: number;
  title: string | null;
  body: string | null;
  authorName: string;
  authorEmail: string | null;
  /** A paid order of this product backs the review. */
  verified: boolean;
  verifiedOrderId: string | null;
  ownerReply: string | null;
  status: ReviewStatus;
  mode: PaymentMode;
  createdAt: string;
  updatedAt: string;
}

const base = (appId: string) => `/api/plugins/reviews/apps/${encodeURIComponent(appId)}`;
const seg = encodeURIComponent;

export interface ReviewSummary {
  subject: string;
  /** Mean approved rating (one decimal), null with no approved reviews. */
  average: number | null;
  count: number;
  pending: number;
}

/** Exact rating summary of one subject (a product slug), over every review. */
export async function apiReviewSummary(appId: string, subject: string): Promise<ReviewSummary> {
  const { data } = await httpClient.get<ReviewSummary>(`${base(appId)}/summary?subject=${seg(subject)}`);

  return data;
}

/** Newest first, up to 100 per page. */
export async function apiListReviews(
  appId: string,
  filter: { status?: ReviewStatus; mode?: PaymentMode; page?: number } = {},
): Promise<ReviewView[]> {
  const params = new URLSearchParams();

  if (filter.status) {
    params.set('status', filter.status);
  }

  if (filter.mode) {
    params.set('mode', filter.mode);
  }

  if (filter.page && filter.page > 1) {
    params.set('page', String(filter.page));
  }

  const query = params.toString();
  const { data } = await httpClient.get<ReviewView[]>(`${base(appId)}${query ? `?${query}` : ''}`);

  return data;
}

export async function apiApproveReview(appId: string, reviewId: string): Promise<ReviewView> {
  const { data } = await httpClient.post<ReviewView>(`${base(appId)}/${seg(reviewId)}/approve`, {});
  return data;
}

export async function apiRejectReview(appId: string, reviewId: string): Promise<ReviewView> {
  const { data } = await httpClient.post<ReviewView>(`${base(appId)}/${seg(reviewId)}/reject`, {});
  return data;
}

/** An empty reply removes it. */
export async function apiReplyToReview(appId: string, reviewId: string, reply: string): Promise<ReviewView> {
  const { data } = await httpClient.post<ReviewView>(`${base(appId)}/${seg(reviewId)}/reply`, { reply });
  return data;
}
